import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, increment } from 'firebase/firestore';
import { db } from '../../core/firebase/firebase';
import { useAuth } from '../../core/firebase/useAuth';
import { useAuthModal } from '../../core/AuthModalContext';
import { productLink, normalizeCategory } from '../../core/categoryUtils';
import { trackLike } from '../../core/taste';
import { markSeen } from '../../core/seenProducts';
import { IconArrowLeft, IconHeart, IconShare } from '../../components/Icons';

const CSS = `
  .pd-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    min-height: calc(100vh - 57px);
  }
  .pd-left {
    position: sticky;
    top: 57px;
    height: calc(100vh - 57px);
    overflow: hidden;
    background: #111116;
  }
  .pd-left .pd-main-img-box {
    width: 100%;
    height: calc(100vh - 57px);
    overflow: hidden;
  }
  .pd-left .pd-main-img-box img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .pd-right {
    overflow-y: auto;
    padding: 28px 28px 120px;
    background: #0f0f13;
  }
  @media (max-width: 767px) {
    .pd-layout {
      grid-template-columns: 1fr;
      min-height: unset;
    }
    .pd-left {
      position: relative;
      top: 0;
      height: auto;
      overflow: hidden;
    }
    .pd-left .pd-main-img-box {
      height: 72vw;
    }
    .pd-right {
      padding: 20px 16px 100px;
    }
  }
`;

export default function ProductDetailPage() {
  const { col, id }       = useParams();
  const { state }         = useLocation();
  const navigate          = useNavigate();
  const { user }          = useAuth();
  const { showAuthModal } = useAuthModal();

  const [product, setProduct] = useState(state?.product || null);
  const [loading, setLoading] = useState(!state?.product);
  const [liked,   setLiked]   = useState(false);
  const [likes,   setLikes]   = useState(0);
  const [saving,  setSaving]  = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    // Tracker en localStorage pour l'algo de reco (cross-session)
    markSeen(col, id);
    // Incrémenter le compteur Firestore une seule fois par session
    const key = `viewed_${col}_${id}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      updateDoc(doc(db, col, id), { views: increment(1) }).catch(() => {});
    }
  }, [col, id]); // eslint-disable-line

  useEffect(() => {
    if (product) {
      setLiked(user ? (product.likedBy || []).includes(user.uid) : false);
      setLikes(product.likes || 0);
      return;
    }
    setLoading(true);
    getDoc(doc(db, col, id))
      .then(snap => {
        if (snap.exists()) {
          const p = { id: snap.id, ...snap.data(), _col: col };
          setProduct(p);
          setLiked(user ? (p.likedBy || []).includes(user.uid) : false);
          setLikes(p.likes || 0);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [col, id]); // eslint-disable-line

  const handleLike = async () => {
    if (!user) { showAuthModal(); return; }
    if (saving || !product) return;
    setSaving(true);
    const ref = doc(db, product._col || col, product.id || id);
    try {
      if (liked) {
        await updateDoc(ref, { likedBy: arrayRemove(user.uid), likes: increment(-1) });
        setLiked(false); setLikes(l => l - 1);
      } else {
        await updateDoc(ref, { likedBy: arrayUnion(user.uid), likes: increment(1) });
        setLiked(true); setLikes(l => l + 1);
        trackLike(normalizeCategory(product.category));
      }
    } catch {}
    finally { setSaving(false); }
  };

  const handleShare = () => {
    const url = productLink(product) || window.location.href;
    if (navigator.share) navigator.share({ title: product?.title, url });
    else navigator.clipboard?.writeText(url);
  };

  if (loading) return (
    <div style={s.page}>
      <style>{CSS}</style>
      <div style={s.topBar}>
        <button style={s.backBtn} onClick={() => navigate(-1)}>
          <IconArrowLeft size={20} color="#fff" />
        </button>
      </div>
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {[360, 18, 14, 14, 40].map((h, i) => (
          <div key={i} style={{ height: h, borderRadius: 12, background: 'linear-gradient(90deg,#1a1a22 25%,#22222e 50%,#1a1a22 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
        ))}
      </div>
    </div>
  );

  if (!product) return (
    <div style={s.page}>
      <style>{CSS}</style>
      <div style={s.topBar}>
        <button style={s.backBtn} onClick={() => navigate(-1)}>
          <IconArrowLeft size={20} color="#fff" />
        </button>
        <span style={s.topTitle}>Produit</span>
      </div>
      <p style={{ textAlign: 'center', color: '#555', paddingTop: 100 }}>Produit introuvable.</p>
    </div>
  );

  // Collecte toutes les images disponibles
  const images = [
    product.imageUrl, product.image,
    ...(product.images || []),
    ...(product.additionalImages || []),
  ].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);

  const mainImg = images[activeImg] || null;

  const price    = product.price ? `$${parseFloat(product.price).toFixed(2)}` : null;
  const link     = productLink(product);
  const creator  = product.isCreator;
  const username = product.username || product.displayName || '';
  const desc     = product.description || product.desc || '';
  const views    = product.views || 0;

  return (
    <div style={s.page}>
      <style>{CSS}</style>

      {/* ── TOP BAR sticky ── */}
      <div style={s.topBar}>
        <button style={s.backBtn} onClick={() => navigate(-1)}>
          <IconArrowLeft size={20} color="#fff" />
        </button>
        <span style={s.topTitle}>Produit</span>
        <div style={s.topRight}>
          <button style={s.circleBtn} onClick={handleShare}>
            <IconShare size={17} color="#ccc" />
          </button>
          <button style={{ ...s.circleBtn, ...(liked ? s.circleLiked : {}) }} onClick={handleLike}>
            <IconHeart size={17} filled={liked} color={liked ? '#ff4d6d' : '#ccc'} />
          </button>
        </div>
      </div>

      {/* ── 2 COLONNES ── */}
      <div className="pd-layout">

        {/* GAUCHE — images */}
        <div className="pd-left">
          {/* image principale */}
          <div className="pd-main-img-box" style={s.mainImgBox}>
            {mainImg
              ? <img src={mainImg} alt={product.title} style={s.mainImg} referrerPolicy="no-referrer" />
              : <div style={s.imgEmpty}><span style={{ fontSize: 48, opacity: 0.1 }}>🖼️</span></div>
            }
            {creator && <div style={s.creatorBadge}>✦ Créateur</div>}
          </div>

          {/* thumbnails si plusieurs images */}
          {images.length > 1 && (
            <div style={s.thumbRow}>
              {images.map((img, i) => (
                <button
                  key={i}
                  style={{ ...s.thumb, ...(i === activeImg ? s.thumbActive : {}) }}
                  onClick={() => setActiveImg(i)}
                >
                  <img src={img} alt="" style={s.thumbImg} referrerPolicy="no-referrer" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* DROITE — infos */}
        <div className="pd-right">

          {/* titre */}
          <h1 style={s.title}>{product.title}</h1>

          {/* prix */}
          {price
            ? <p style={s.price}>{price}</p>
            : <p style={s.noPrice}>Prix non listé</p>
          }

          {/* stats */}
          <div style={s.stats}>
            <div style={s.statChip}>❤️ <strong>{likes}</strong> likes</div>
            <div style={s.statChip}>👁 <strong>{views}</strong> vues</div>
            {product.condition && (
              <div style={s.statChip}>
                {product.condition === 'NEW' ? '✨ Neuf' : '📦 Occasion'}
              </div>
            )}
          </div>

          {/* vendeur */}
          <div style={s.sellerCard}>
            <div style={s.sellerAvatar}>
              {creator && product.photoURL
                ? <img src={product.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={s.sellerInitial}>{creator ? (username[0]?.toUpperCase() || '?') : 'e'}</span>
              }
            </div>
            <div style={s.sellerInfo}>
              <span style={s.sellerName}>{creator ? `@${username}` : 'eBay'}</span>
              <span style={s.sellerSub}>{creator ? 'Créateur indépendant' : 'Vendeur officiel'}</span>
            </div>
            {product.category && <span style={s.catTag}>{product.category}</span>}
          </div>

          {/* description */}
          {desc && (
            <div style={s.descBox}>
              <p style={s.descLabel}>Description</p>
              <p style={s.descText}>{desc}</p>
            </div>
          )}

          {/* bouton */}
          {link && (
            <button style={s.buyBtn} onClick={() => setConfirm(true)}>
              Voir le produit →
            </button>
          )}

        </div>
      </div>

      {/* ── MODAL ── */}
      {confirm && (
        <>
          <div style={s.overlay} onClick={() => setConfirm(false)} />
          <div style={s.modal}>
            <div style={s.modalBar} />
            <p style={s.modalTitle}>Continuer sur le site ?</p>
            <p style={s.modalSub}>Tu vas être redirigé vers un site externe.</p>
            {mainImg && (
              <div style={s.modalPreview}>
                <img src={mainImg} alt="" style={s.modalThumb} referrerPolicy="no-referrer" />
                <div>
                  <p style={s.modalProdTitle}>{product.title?.slice(0, 55)}{product.title?.length > 55 ? '…' : ''}</p>
                  <p style={s.modalProdPrice}>{price || '—'}</p>
                </div>
              </div>
            )}
            <div style={s.modalBtns}>
              <button style={s.btnCancel} onClick={() => setConfirm(false)}>Annuler</button>
              <button style={s.btnGo} onClick={() => { window.open(link, '_blank', 'noopener'); setConfirm(false); }}>
                Continuer →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const s = {
  page     : { background: '#0f0f13', minHeight: '100vh' },

  topBar   : { display: 'flex', alignItems: 'center', padding: '12px 16px', background: '#0f0f13', position: 'sticky', top: 0, zIndex: 20, borderBottom: '1px solid rgba(255,255,255,0.05)', height: 57 },
  backBtn  : { width: 38, height: 38, borderRadius: 19, background: 'rgba(255,255,255,0.07)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  topTitle : { flex: 1, fontSize: 16, fontWeight: 700, color: '#fff', paddingLeft: 12 },
  topRight : { display: 'flex', gap: 8 },
  circleBtn: { width: 38, height: 38, borderRadius: 19, background: 'rgba(255,255,255,0.07)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  circleLiked: { background: 'rgba(255,77,109,0.15)' },

  // Images (gauche)
  mainImgBox  : { position: 'relative', width: '100%', overflow: 'hidden' },
  mainImg     : { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  imgEmpty    : { width: '100%', height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111116' },
  creatorBadge: { position: 'absolute', top: 12, left: 12, background: 'rgba(67,97,238,0.88)', backdropFilter: 'blur(6px)', borderRadius: 99, padding: '5px 14px', fontSize: 11, fontWeight: 700, color: '#fff' },

  thumbRow : { display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px' },
  thumb    : { width: 62, height: 62, borderRadius: 10, overflow: 'hidden', border: '2px solid transparent', background: 'none', cursor: 'pointer', padding: 0, flexShrink: 0 },
  thumbActive: { border: '2px solid #4361ee' },
  thumbImg : { width: '100%', height: '100%', objectFit: 'cover' },

  // Infos (droite)
  title    : { fontSize: 20, fontWeight: 800, color: '#fff', margin: '0 0 10px', lineHeight: 1.4 },
  price    : { fontSize: 30, fontWeight: 900, color: '#4361ee', margin: '0 0 16px' },
  noPrice  : { fontSize: 13, color: '#444', margin: '0 0 16px' },

  stats    : { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 },
  statChip : { background: 'rgba(255,255,255,0.06)', borderRadius: 99, padding: '6px 14px', fontSize: 13, color: '#aaa', display: 'flex', gap: 5, alignItems: 'center' },

  sellerCard  : { display: 'flex', alignItems: 'center', gap: 12, background: '#1a1a22', borderRadius: 14, padding: '14px', marginBottom: 18, border: '1px solid rgba(255,255,255,0.05)' },
  sellerAvatar: { width: 44, height: 44, borderRadius: 22, background: '#4361ee', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 },
  sellerInitial: { fontSize: 17, fontWeight: 700, color: '#fff' },
  sellerInfo  : { flex: 1, display: 'flex', flexDirection: 'column', gap: 2 },
  sellerName  : { fontSize: 14, fontWeight: 700, color: '#fff' },
  sellerSub   : { fontSize: 11, color: '#555' },
  catTag      : { background: 'rgba(67,97,238,0.15)', color: '#6a82fb', fontSize: 11, fontWeight: 700, borderRadius: 99, padding: '4px 12px', border: '1px solid rgba(67,97,238,0.2)' },

  descBox  : { background: '#1a1a22', borderRadius: 14, padding: '16px 18px', marginBottom: 22, border: '1px solid rgba(255,255,255,0.05)' },
  descLabel: { fontSize: 11, fontWeight: 700, color: '#4361ee', textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 10px' },
  descText : { fontSize: 14, color: '#888', lineHeight: 1.75, margin: 0 },

  buyBtn   : { width: '100%', height: 52, background: '#4361ee', color: '#fff', border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 700, cursor: 'pointer', boxShadow: '0 6px 22px rgba(67,97,238,0.45)' },

  // Modal
  overlay      : { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 200, backdropFilter: 'blur(4px)' },
  modal        : { position: 'fixed', bottom: 0, left: 0, right: 0, background: '#13131a', borderRadius: '22px 22px 0 0', padding: '12px 20px 44px', zIndex: 201, border: '1px solid rgba(255,255,255,0.07)' },
  modalBar     : { width: 40, height: 4, background: '#2a2a35', borderRadius: 2, margin: '0 auto 18px' },
  modalTitle   : { fontSize: 18, fontWeight: 800, color: '#fff', margin: '0 0 5px' },
  modalSub     : { fontSize: 13, color: '#555', margin: '0 0 18px' },
  modalPreview : { display: 'flex', gap: 12, background: '#1a1a22', borderRadius: 12, padding: 12, marginBottom: 20 },
  modalThumb   : { width: 56, height: 56, objectFit: 'cover', borderRadius: 10, flexShrink: 0 },
  modalProdTitle:{ fontSize: 13, fontWeight: 600, color: '#ddd', margin: '0 0 4px', lineHeight: 1.3 },
  modalProdPrice:{ fontSize: 16, fontWeight: 800, color: '#4361ee', margin: 0 },
  modalBtns    : { display: 'flex', gap: 10 },
  btnCancel    : { flex: 1, background: '#1a1a22', color: '#666', border: 'none', borderRadius: 13, height: 50, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  btnGo        : { flex: 2, background: '#4361ee', color: '#fff', border: 'none', borderRadius: 13, height: 50, fontSize: 15, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px rgba(67,97,238,0.4)' },
};
